import styled, { css } from 'styled-components';
import BottomModal from '../modal/BottomModal';
import { RegionProps, SidoList } from '../../search/FilterData';
import { useEffect, useState } from 'react';
import RegionService from '../../../service/Region';
import {
  ConfirmButton,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../modal/Modal.style';

interface Props {
  initialRegion?: {
    pnu: string;
    sido: string;
    sigungu: string;
    emd: string;
  };
  sidoList?: RegionProps[];
  disableList?: RegionProps[];
  onClose: () => void;
  onSelectRegion: (region: {
    pnu: string;
    sido: string;
    sigungu: string;
    emd: string;
  }) => void;
}

function RegionSelectModal({
  initialRegion,
  sidoList,
  disableList,
  onSelectRegion,
  onClose,
}: Props) {
  const [pnu, setPnu] = useState(initialRegion?.pnu ?? '');
  const [sido, setSido] = useState<RegionProps | null>(null);
  const [sigungu, setSigungu] = useState<RegionProps | null>(null);
  const [emd, setEmd] = useState<RegionProps | null>(null);
  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);

  const getRegion = async (code: string) => {
    try {
      const res = await RegionService.list(code);
      return res.data as RegionProps[];
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  useEffect(() => {
    if (sido !== null) {
      getRegion(sido.code).then((res) => setSigunguList(res));
    }
  }, [sido]);

  useEffect(() => {
    if (sigungu !== null && sigungu.region !== '전체') {
      getRegion(sigungu.code).then((res) => setEmdList(res));
    }
  }, [sigungu]);

  useEffect(() => {
    const initSido =
      SidoList.find((item) => item.region === initialRegion?.sido) || null;
    setSido(initSido);

    if (!initSido) return;
    getRegion(initSido.code).then((sigunguRes) => {
      setSigunguList(sigunguRes);
      const initSigungu =
        sigunguRes.find((item) => item.region === initialRegion?.sigungu) ||
        null;
      setSigungu(initSigungu);

      if (initSigungu) {
        getRegion(initSigungu.code).then((emdRes) => {
          setEmdList(emdRes);
          const filteredEmds = emdRes.filter((item) => item.region !== '전체');
          const initEmd =
            filteredEmds.find((item) => item.region === initialRegion?.emd) ||
            null;
          setEmd(initEmd);
        });
      }
    });
  }, [initialRegion]);

  const selectRegionLabel = emd
    ? `${sigungu?.region} ${emd.region}`
    : sigungu
      ? `${sido?.region} ${sigungu.region}`
      : sido?.region;

  const handleReset = () => {
    setPnu('');
    setSido(null);
    setSigungu(null);
    setEmd(null);
    setSigunguList([]);
    setEmdList([]);
  };
  const handleSubmit = () => {
    onSelectRegion({
      pnu: pnu,
      sido: sido?.region ?? '',
      sigungu: sigungu?.region ?? '',
      emd: emd?.region ?? '',
    });
    onClose();
  };

  const sidos = sidoList ? sidoList : SidoList;

  return (
    <BottomModal onClose={onClose}>
      <ModalHeader>
        <ModalTitle>지역 설정</ModalTitle>
        <ModalCloseButton
          onClick={onClose}
          src="/assets/images/close/close-dark-gray.svg"
          alt="close"
        />
      </ModalHeader>
      <SelectedSection>
        <SelectContainer>
          <SelectOption $active={!!sido}>
            {sido ? sido.region : '도/시 (필수)'}
          </SelectOption>
          <img
            src="/assets/images/arrow/arrow_right.svg"
            alt=""
            style={{ width: '24px', height: '24px' }}
          />
          <SelectOption $active={!!sigungu}>
            {sigungu ? sigungu.region : '시/군/구'}
          </SelectOption>
          <img
            src="/assets/images/arrow/arrow_right.svg"
            alt=""
            style={{ width: '24px', height: '24px' }}
          />
          <SelectOption $active={!!emd}>
            {emd ? emd.region : '읍/면/동'}
          </SelectOption>
        </SelectContainer>
      </SelectedSection>
      <RegionSelector>
        <SidoColumn>
          {sidos.map((item) => (
            <SidoItem
              key={item.code}
              $active={item === sido}
              $disable={disableList?.includes(item)}
              onClick={() => {
                if (sido?.code === item.code) return;
                setPnu(item.code);
                setSido(item);
                setSigungu(null);
                setEmd(null);
                setSigunguList([]);
                setEmdList([]);
              }}
            >
              {item.region}
            </SidoItem>
          ))}
        </SidoColumn>
        <Column>
          {sigunguList.length > 0 &&
            [{ code: sido?.code ?? '', region: '전체' }, ...sigunguList].map(
              (item) => (
                <Item
                  key={item.code}
                  $active={
                    item.region === '전체'
                      ? !sigungu
                      : item.code === sigungu?.code
                  }
                  onClick={() => {
                    if (sigungu?.code === item.code) return;
                    if (item.region === '전체') {
                      setSigungu(null);
                      setEmd(null);
                      setEmdList([]);
                    } else {
                      setEmd(null);
                      setEmdList([]);
                      setPnu(item.code);
                      setSigungu(item);
                    }
                  }}
                >
                  {item.region}
                </Item>
              ),
            )}
        </Column>
        <Column>
          {emdList.map((item) => (
            <Item
              key={item.code}
              $active={item.code === emd?.code}
              onClick={() => {
                setPnu(item.code);
                setEmd(item);
              }}
            >
              {item.region}
            </Item>
          ))}
        </Column>
      </RegionSelector>

      <ModalFooter>
        {/* <ButtonBox> */}
        <ResetButton onClick={handleReset}>
          <img
            src="/assets/images/icon/reset.svg"
            alt=""
            width={24}
            height={24}
          />
          <span>초기화</span>
        </ResetButton>
        <ConfirmButton $disabled={pnu === ''} onClick={handleSubmit}>
          {pnu ? `${selectRegionLabel} 적용` : '지역 선택'}
        </ConfirmButton>
        {/* </ButtonBox> */}
      </ModalFooter>
    </BottomModal>
  );
}

export default RegionSelectModal;

const SelectedSection = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 16px;
  border-bottom: 1px solid #e1e2e4;
`;

const SelectContainer = styled.div`
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  background: rgba(112, 115, 124, 0.05);
`;

const SelectOption = styled.div<{ $active?: boolean }>`
  flex: 1 0 0;
  color: rgba(55, 56, 60, 0.61);
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;

  ${({ $active }) => $active && 'color: #222'}
`;

const RegionSelector = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1px;
  /* height: 100px; */
  flex: 1;
  overflow: hidden;
`;

const Column = styled.div`
  background-color: #fff;
  flex: 1;
  overflow-y: auto;
  border-right: 1px solid #e1e2e4;
`;

const SidoColumn = styled.div`
  width: 80px;
  background-color: #f5f5f5;
  overflow-y: auto;
  border-right: 1px solid #e1e2e4;
`;

const Item = styled.div<{ $active: boolean; $disable?: boolean }>`
  padding: 12px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#008AFF' : '#222')};
  background-color: ${({ $active }) => ($active ? '#EAF2FE' : '#fff')};
  font-family: Pretendard;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  text-align: center;
`;

const SidoItem = styled(Item)`
  color: ${({ $active }) => ($active ? '#222222' : '#37383C9C')};
  background-color: ${({ $active }) => ($active ? '#FFFFFF' : '#F5F5F5')};

  ${({ $disable }) =>
    $disable &&
    `
      color: #37383C29;
      pointer-events: none;
    `};
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #70737c38;
  gap: 16px;
  padding: 16px;
`;
const ButtonBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const ResetButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #70737c38;
  background-color: #fff;
  font-size: 10px;
  font-weight: 500;
  color: #37383c9c;
  cursor: pointer;
  padding: 7px 16px;
`;

const ApplyButton = styled.button<{ $disabled: boolean }>`
  flex: 1;
  border-radius: 8px;
  border: none;
  padding: 14px 28px;
  background-color: #008aff;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;

  ${({ $disabled }) =>
    $disabled &&
    css`
      background: #f7f7f8;
      color: #37383c29;
      pointer-events: none;
    `}
`;
